'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useModal } from '@src/components/Modal/ModalProvider';
import type { Sierra } from '@src/components/Sierra/types';
import { configPublic } from '@src/config.public';
import { useLockScroll } from '@src/hooks/useLockScroll';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { getUnlocalizedPath } from '@src/i18n/locale.util';
import { trackOpenChat, trackStartConversation } from '@src/utils/analytics.util';
import { APP_HASH, COOKIES } from '@src/utils/constants.util';
import { getBrowserCookieDomain, removeBrowserCookie } from '@src/utils/cookies.util';
import { log } from '@src/utils/log.util';
interface SierraContext {
  sierra?: Sierra;
}
interface SierraProviderProps {
  children: ReactNode;
}
interface SierraIdentity {
  token: string | null;
  variables: Record<string, string>;
}
const ANONYMOUS_IDENTITY: SierraIdentity = {
  token: null,
  variables: {}
};

/** Resolves to an anonymous identity on failure so the chat never stalls. */
const fetchSierraIdentity = async (): Promise<SierraIdentity> => {
  try {
    const response = await fetch('/api/sierra/identity-token');
    const data = (await response.json()) as Partial<SierraIdentity>;
    return {
      token: data.token ?? null,
      variables: data.variables ?? {}
    };
  } catch {
    return ANONYMOUS_IDENTITY;
  }
};

// Unverified read is safe: it only detects identity changes; Sierra verifies the
// token itself.
const getTokenSubject = (token: string | null): string | null => {
  const payload = token?.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    return (JSON.parse(atob(padded)) as {
      sub?: string;
    }).sub ?? null;
  } catch {
    return null;
  }
};
const SierraContext = createContext<SierraContext>({});
export const SierraProvider = props => {
  const $ = _c(44);
  const {
    children
  } = props;
  const {
    locale
  } = useLocale();
  const {
    authSession,
    isComingFromLogout
  } = useSession();
  const modal = useModal();
  const [sierra, setSierra] = useState();
  const [isOpen, setIsOpen] = useState(false);
  const [userIdentityToken, setUserIdentityToken] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [sierraVariables, setSierraVariables] = useState(t0);
  const [isIdentityResolved, setIsIdentityResolved] = useState(false);
  const [reinitCount, setReinitCount] = useState(0);
  const hasInitialized = useRef(false);
  const initializedSubject = useRef(null);
  const hasClearedOnLogout = useRef(false);
  const pathname = usePathname();
  let t1;
  if ($[1] !== isOpen) {
    t1 = {
      isExpanded: isOpen
    };
    $[1] = isOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useLockScroll(t1);
  const isSierraEnabled = configPublic.sierra.isEnabled && locale.code === "en-US";
  let t2;
  if ($[3] !== authSession?.access_token || $[4] !== isSierraEnabled) {
    t2 = () => {
      if (!isSierraEnabled) {
        return;
      }
      let isActive = true;
      const resolveIdentity = async () => {
        const identity = authSession?.access_token ? await fetchSierraIdentity() : ANONYMOUS_IDENTITY;
        if (!isActive) {
          return;
        }
        setUserIdentityToken(identity.token);
        setSierraVariables(identity.variables);
        setIsIdentityResolved(true);
      };
      resolveIdentity();
      return () => {
        isActive = false;
      };
    };
    $[3] = authSession?.access_token;
    $[4] = isSierraEnabled;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = authSession?.access_token;
  let t4;
  if ($[6] !== isSierraEnabled || $[7] !== t3) {
    t4 = [isSierraEnabled, t3];
    $[6] = isSierraEnabled;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t2, t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(_temp, t5);
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = async () => {
      const {
        token
      } = await fetchSierraIdentity();
      if (!token) {
        log.warn("[Sierra] Could not refresh the user identity token. Continuing anonymously.");
        return null;
      }
      initializedSubject.current = getTokenSubject(token);
      return token;
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const refreshUserIdentityToken = t6;
  let t7;
  let t8;
  if ($[11] !== isComingFromLogout || $[12] !== sierra) {
    t7 = () => {
      if (!isComingFromLogout || hasClearedOnLogout.current) {
        return;
      }
      hasClearedOnLogout.current = true;
      removeBrowserCookie(COOKIES.sierraChatSession);
      if (sierra && hasInitialized.current) {
        sierra.clearPersistedConversation();
        sierra.cleanup();
        hasInitialized.current = false;
        initializedSubject.current = null;
        setReinitCount(_temp2);
      }
    };
    t8 = [sierra, isComingFromLogout];
    $[11] = isComingFromLogout;
    $[12] = sierra;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] !== isIdentityResolved || $[16] !== sierra || $[17] !== sierraVariables || $[18] !== userIdentityToken) {
    t9 = () => {
      if (!sierra || !isIdentityResolved) {
        return;
      }
      const token_0 = userIdentityToken ?? undefined;
      const subject = getTokenSubject(userIdentityToken);
      if (hasInitialized.current) {
        if (initializedSubject.current === subject) {
          return;
        }
        sierra.cleanup();
      }
      sierra.init({
        userIdentityToken: token_0,
        onUserIdentityTokenExpiry: refreshUserIdentityToken,
        variables: sierraVariables,
        persistence: "cookie",
        cookieDomain: getBrowserCookieDomain(),
        cookieName: COOKIES.sierraChatSession,
        colors: {
          background: "#ffffff",
          text: "#161819",
          border: "#A09EA3",
          titleBar: "#161819",
          titleBarText: "#ffffff",
          assistantBubble: "#EDFAFF",
          assistantBubbleText: "#164646",
          userBubble: "#d8dbdf",
          userBubbleText: "#4a4e5a"
        },
        display: "corner",
        hideOnClose: true,
        canEndConversation: true,
        canStartNewChat: true,
        onOpen(opts) {
          trackOpenChat(opts.isNewConversation);
          setIsOpen(true);
        },
        onClose() {
          setIsOpen(false);
          if (location.hash === `#${APP_HASH.chat}`) {
            window.history.replaceState({}, "", location.pathname + location.search);
          }
        },
        onConversationStart(opts_0) {
          trackStartConversation(opts_0.conversationID);
        }
      });
      hasInitialized.current = true;
      initializedSubject.current = subject;
    };
    $[15] = isIdentityResolved;
    $[16] = sierra;
    $[17] = sierraVariables;
    $[18] = userIdentityToken;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isIdentityResolved || $[21] !== reinitCount || $[22] !== sierra || $[23] !== sierraVariables || $[24] !== userIdentityToken) {
    t10 = [sierra, isIdentityResolved, userIdentityToken, sierraVariables, refreshUserIdentityToken, reinitCount];
    $[20] = isIdentityResolved;
    $[21] = reinitCount;
    $[22] = sierra;
    $[23] = sierraVariables;
    $[24] = userIdentityToken;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[26] !== pathname || $[27] !== sierra) {
    t11 = () => {
      const unlocalizedPathname = getUnlocalizedPath(pathname);
      if (configPublic.sierra.pathsToCloseChat.includes(unlocalizedPathname)) {
        sierra?.closeChatModal();
      }
    };
    t12 = [pathname, sierra];
    $[26] = pathname;
    $[27] = sierra;
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[30] !== modal || $[31] !== sierra) {
    t13 = () => {
      if (modal.isOpen(APP_HASH.chat) && location.hash === `#${APP_HASH.chat}` && !!sierra) {
        sierra.openChatModal();
      }
    };
    t14 = [modal, sierra];
    $[30] = modal;
    $[31] = sierra;
    $[32] = t13;
    $[33] = t14;
  } else {
    t13 = $[32];
    t14 = $[33];
  }
  useEffect(t13, t14);
  let t15;
  if ($[34] !== sierra) {
    t15 = {
      sierra
    };
    $[34] = sierra;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== isSierraEnabled) {
    t16 = isSierraEnabled && <Script type="module" strategy="afterInteractive" crossOrigin="anonymous" src={configPublic.sierra.scriptUrl} onLoad={() => {
      setSierra(window?.sierra);
    }} />;
    $[36] = isSierraEnabled;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== children || $[39] !== t16) {
    t17 = <>{t16}{children}</>;
    $[38] = children;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t15 || $[42] !== t17) {
    t18 = <SierraContext.Provider value={t15}>{t17}</SierraContext.Provider>;
    $[41] = t15;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  return t18;
};
export const useSierra = () => {
  const context = useContext(SierraContext);
  if (!context) {
    throw new Error("useSierra must be used within SierraContext");
  }
  return context;
};
function _temp() {
  window.sierraConfig = {
    waitForInit: true
  };
}
function _temp2(count) {
  return count + 1;
}